/* eslint-disable no-underscore-dangle */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  DataEntryWithTask,
  analyzeTaskCancellation,
  analyzeTaskCreation,
  validateTaskCreation,
  validateTaskCancellation,
  createConfirmCategoryTasks,
  cancelConfirmCategoryTasks,
  getTaskOperationMessages,
} from '../components/task-operations/taskOperations';
import { NotificationService, notificationService } from '../services/notificationService';

export enum TaskOperationState {
  IDLE = 'IDLE',
  CREATING = 'CREATING',
  CANCELLING = 'CANCELLING',
}

export interface UseTaskOperationsProps {
  notificationService?: NotificationService;
}

export interface UseTaskOperationsReturn {
  taskOperationState: TaskOperationState;
  createTasks: (params: CreateTasksParams) => (onSuccess?: () => void) => void;
  cancelTasks: (params: CancelTasksParams) => (onSuccess?: () => void) => void;
  canCancelTasks: (params: CancelTasksParams) => boolean;
  canCreateTasks: (params: CreateTasksParams) => boolean;
}

interface CreateTasksParams {
  taskType: string | null;
  dataEntries: DataEntryWithTask[];
  clientId: string;
  personId: string;
  isFeatureEnabled: boolean;
}

interface CancelTasksParams {
  taskType: string | null;
  dataEntries: DataEntryWithTask[];
  clientId: string;
  isFeatureEnabled: boolean;
}

export function useTaskOperations(props: UseTaskOperationsProps = {}): UseTaskOperationsReturn {
  const { notificationService: notifyService = notificationService } = props;

  const [taskOperationState, setTaskOperationState] = useState<TaskOperationState>(
    TaskOperationState.IDLE,
  );

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const createTasks = useCallback(
    (params: CreateTasksParams) => {
      return (onSuccess?: () => void) => {
        if (!isMounted.current) return;

        const validation = validateTaskCreation({
          taskType: params.taskType,
          dataEntriesCount: params.dataEntries.length,
          isFeatureEnabled: params.isFeatureEnabled,
        });

        if (!validation.isValid) {
          notifyService.showError('Cannot create tasks', validation.reason || 'Invalid parameters');
          return;
        }

        setTaskOperationState(TaskOperationState.CREATING);

        const dataEntryIds = params.dataEntries.map(entry => entry.id);
        const dataEntryToLedgerIdMap = new Map<string, string>();
        params.dataEntries.forEach(entry => {
          if (entry.ledgerAccount?.id) {
            dataEntryToLedgerIdMap.set(entry.id, entry.ledgerAccount.id);
          }
        });

        createConfirmCategoryTasks(dataEntryIds, params.clientId, dataEntryToLedgerIdMap)
          .then(result => {
            if (!isMounted.current) return;

            if (result.success) {
              const messages = getTaskOperationMessages('create', undefined, result);
              // Check if this is a partial success (some failed)
              const hasPartialSuccess =
                result.data?.__typename === 'ConfirmCategoryComplete' &&
                result.data.failed &&
                result.data.failed.length > 0;

              if (hasPartialSuccess) {
                notifyService.showWarning(messages.success.title, messages.success.message);
              } else {
                notifyService.showSuccess(messages.success.title, messages.success.message);
              }
              onSuccess?.();
            } else {
              const messages = getTaskOperationMessages('create', undefined, result);
              notifyService.showError(messages.error.title, messages.error.message);
            }
          })
          .catch(error => {
            if (!isMounted.current) return;
            const messages = getTaskOperationMessages('create', undefined, {
              success: false,
              error: error.message,
            });
            notifyService.showError(messages.error.title, messages.error.message);
          })
          .finally(() => {
            if (isMounted.current) {
              setTaskOperationState(TaskOperationState.IDLE);
            }
          });
      };
    },
    [notifyService],
  );

  const cancelTasks = useCallback(
    (params: CancelTasksParams) => {
      return (onSuccess?: () => void) => {
        if (!isMounted.current) return;

        const validation = validateTaskCancellation({
          taskType: params.taskType,
          dataEntriesCount: params.dataEntries.length,
          isFeatureEnabled: params.isFeatureEnabled,
        });

        if (!validation.isValid) {
          notifyService.showError('Cannot cancel tasks', validation.reason || 'Invalid parameters');
          return;
        }

        const summary = analyzeTaskCancellation(params.dataEntries);

        if (summary.totalCount === 0) {
          return;
        }

        setTaskOperationState(TaskOperationState.CANCELLING);

        cancelConfirmCategoryTasks(summary.taskIds, params.clientId)
          .then(result => {
            if (!isMounted.current) return;

            if (result.success) {
              const messages = getTaskOperationMessages('cancel', summary, result);
              // Check if this is a partial success (some failed)
              const hasPartialSuccess =
                result.data?.__typename === 'ResolveTasksComplete' &&
                result.data.failedTasks &&
                result.data.failedTasks.length > 0;

              if (hasPartialSuccess) {
                notifyService.showWarning(messages.success.title, messages.success.message);
              } else {
                notifyService.showSuccess(messages.success.title, messages.success.message);
              }
              onSuccess?.();
            } else {
              const messages = getTaskOperationMessages('cancel', summary, result);
              notifyService.showError(messages.error.title, messages.error.message);
            }
          })
          .catch(error => {
            if (!isMounted.current) return;
            const messages = getTaskOperationMessages('cancel', summary, {
              success: false,
              error: error.message,
            });
            notifyService.showError(messages.error.title, messages.error.message);
          })
          .finally(() => {
            if (isMounted.current) {
              setTaskOperationState(TaskOperationState.IDLE);
            }
          });
      };
    },
    [notifyService],
  );

  const canCancelTasks = useCallback((params: CancelTasksParams): boolean => {
    const validation = validateTaskCancellation({
      taskType: params.taskType,
      dataEntriesCount: params.dataEntries.length,
      isFeatureEnabled: params.isFeatureEnabled,
    });

    if (!validation.isValid) {
      return false;
    }

    const summary = analyzeTaskCancellation(params.dataEntries);
    return summary.totalCount > 0;
  }, []);

  const canCreateTasks = useCallback((params: CreateTasksParams): boolean => {
    const validation = validateTaskCreation({
      taskType: params.taskType,
      dataEntriesCount: params.dataEntries.length,
      isFeatureEnabled: params.isFeatureEnabled,
    });

    if (!validation.isValid) {
      return false;
    }

    const summary = analyzeTaskCreation(params.dataEntries);
    return summary.totalCount > 0;
  }, []);

  return {
    taskOperationState,
    createTasks,
    cancelTasks,
    canCancelTasks,
    canCreateTasks,
  };
}
